import React from "react";
import ReactDOM from "react-dom";
import { attachPrint, FloatingPortalContext } from "../componentsUtils/PortalHelpers";
import { WindowManagerPopup } from "./WindowManager";
import { ManagedWindow } from "./WindowState";

export class WindowPortal extends React.PureComponent<{ children: React.ReactNode, managedWindow?: ManagedWindow, adm?: any, title: string, active: boolean, onClose?: () => void }> {
   containerEl: HTMLDivElement;
   externalWindow: Window | null;

   constructor(props: { children: React.ReactNode, onClose?: () => void, active: boolean, title: string }) {
      super(props);
      this.containerEl = document.createElement('div');
      this.containerEl.className = "POPOUT_WINDOW";
      this.externalWindow = null;
   }

   render() {
      // Only render portal if active
      if (!this.props.active)
         return (this.props.children);
      return ReactDOM.createPortal(
         <FloatingPortalContext.Provider value={{ root: this.containerEl, window: this.externalWindow ?? window }}>
            <WindowManagerPopup>
               {this.props.children}
            </WindowManagerPopup>
         </FloatingPortalContext.Provider>
         , this.containerEl);
   }

   componentDidMount() {
      if (this.props.active) {
         this.openWindow();
         window.addEventListener('beforeunload', () => {
            try { this.closeWindow(); } catch { console.warn("window already closed") }
         });
      }
   }

   componentDidUpdate(prevProps: { active: boolean, adm?: any }) {
      if (this.props.active && !prevProps.active) {
         this.openWindow();
      } else if (!this.props.active && prevProps.active) {
         this.closeWindow();
      } else if (this.props.adm && this.props.adm !== prevProps.adm && this.externalWindow?.moveTo && this.externalWindow?.resizeTo) {
         let [x, y] = [this.props.adm?.posDefault?.left, this.props.adm?.posDefault?.top].map((pos) => {
            return typeof pos === "string" && pos.endsWith("px") ? parseInt(pos) : pos;
         });
         this.externalWindow.moveTo(x, y);
         let [width, height] = [this.props.adm?.posDefault?.width, this.props.adm?.posDefault?.height].map((size) => {
            return typeof size === "string" && size.endsWith("px") ? parseInt(size) : size;
         });
         this.externalWindow.resizeTo(width, height);
      }
   }

   componentWillUnmount() {
      this.closeWindow();
   }

   focusWindow() {
      if (this.externalWindow) {
         this.externalWindow.focus();
      }
   }

   openWindow() {
      // STEP 3: open a new browser window and store a reference to it
      // positionShouldBeCalculated = true;
      let x = window.screenX + 50;
      let y = window.screenY + 50;
      let width = window.innerWidth - 100;
      let height = window.innerHeight - 100;

      if (!this.externalWindow || this.externalWindow.closed) {
         console.log(`Opening window at ${x},${y} with size ${width}x${height}`);
         this.externalWindow = window.open('', `${this.props.managedWindow?.id}`, `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},left=${x},top=${y}`);
      }

      // STEP 4: append the container <div> (that has props.children appended to it) to the body of the new window
      this.externalWindow?.document.body.appendChild(this.containerEl);

      if (this.externalWindow?.document) {
         attachPrint(this.externalWindow?.document.body, this.externalWindow);
      }

      if (this.externalWindow?.document)
         this.externalWindow.document.title = this.props.title;

      copyStyles(document, this.externalWindow?.document);

      this.externalWindow?.addEventListener('beforeunload', () => {
         this.props.onClose?.();
         this.closeWindow();
      });
   }

   closeWindow() {
      this.externalWindow?.close?.();
      this.externalWindow = null;
   }
}

function copyStyles(sourceDoc, targetDoc) {
   Array.from(sourceDoc.styleSheets).forEach((styleSheet: unknown) => {
      try {
         if (isCssStyleSheet(styleSheet)) {
            const newStyleEl = sourceDoc.createElement('style');

            Array.from(styleSheet.cssRules).forEach(cssRule => {
               newStyleEl.appendChild(sourceDoc.createTextNode(cssRule.cssText));
            });

            targetDoc.head.appendChild(newStyleEl);
         } else if (typeof styleSheet === 'object' && styleSheet !== null && 'href' in styleSheet) {
            const newLinkEl = sourceDoc.createElement('link');
            newLinkEl.rel = 'stylesheet';
            newLinkEl.href = styleSheet.href;
            targetDoc.head.appendChild(newLinkEl);
         }
      } catch (e) {
         console.error(e);
      }
   });
}

function isCssStyleSheet(styleSheet: unknown): styleSheet is CSSStyleSheet {
   try {
      return (styleSheet as CSSStyleSheet)?.cssRules !== undefined;
   } catch (e) {
      let err = e as DOMException;
      if (err.name === 'SecurityError') {
         // Fjerne fejlbeskeder grunder cors restriktioner. Dette skal måske håndteres anderledes i fremtiden.
         return false;
      }
      console.error(e);
      return false;
   }
}